/**
 * What the events, event and people pages share: the header with the Mawkeb mark and the account
 * menu (people for admins, changing a password, signing out), and the offline note.
 */

import { PASSWORD_MIN } from '@mawkeb/shared/api';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError, forgetHalls } from '../../api';
import { navigate } from '../../router';
import { cacheClear, cacheKeys } from '../../state/cache';
import { session, useSession } from '../../state/session';
import { useVenue, venue } from '../../state/store';
import { Dialog, Menu } from '../Dialog';
import { Offline } from '../icons';

export function Link({ to, children, className, ...rest }: { to: string; children: ReactNode; className?: string; 'aria-label'?: string; title?: string }) {
  return (
    <a
      href={to}
      className={className}
      {...rest}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '')).toUpperCase() || '?';
}

export async function signOut() {
  const drafts = await cacheKeys('draft:');
  if (drafts.length && !confirm(`This device has unsaved changes to ${drafts.length === 1 ? 'a layout' : `${drafts.length} layouts`}. Signing out discards them. Sign out anyway?`)) return;
  try {
    await api.signOut();
  } catch {
    /* offline: the device forgets it all the same, and the session runs out on the server */
  }
  await cacheClear();
  await forgetHalls();
  session().signedOut();
  navigate('/', { replace: true, force: true });
}

function ChangePassword({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api.changePassword(current, next);
      venue().toast('Password changed. Your other devices are signed out.');
      onClose();
    } catch (err) {
      setProblem((err as ApiError).status === 'offline' ? 'There’s no connection. Try again when you’re back online.' : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="Change your password"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="change-password" className="primary" disabled={busy || !current || next.length < PASSWORD_MIN}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </>
      }
    >
      <form id="change-password" className="stack" onSubmit={submit}>
        <input type="email" autoComplete="username" value={session().me?.email ?? ''} readOnly hidden />
        <label className="input">
          <span>Current password</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </label>
        <label className="input">
          <span>New password</span>
          <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
        </label>
        <p className="form-note">At least {PASSWORD_MIN} characters. Your other devices will be signed out.</p>
        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}

export function PageHeader() {
  const me = useSession((s) => s.me);
  const online = useVenue((s) => s.online);
  const [changing, setChanging] = useState(false);
  return (
    <header className="page-header">
      <Link to="/" className="brand small" aria-label="Venue: the events">
        <img src="/icons/icon-192.png" alt="" width="32" height="32" />
        <strong>Venue</strong>
      </Link>
      <div className="page-header-end">
        {!online && (
          <span className="tag quiet" title="No connection. What this device saved still opens.">
            <Offline /> Offline
          </span>
        )}
        {me && (
          <div className="account">
            <span className="account-name">{me.name}</span>
            <Menu
              label={`${me.name}: account`}
              icon={
                <span className="avatar" aria-hidden="true">
                  {initials(me.name)}
                </span>
              }
              items={[
                me.role === 'admin' ? { label: 'People who can sign in', run: () => navigate('/people') } : null,
                { label: 'Change password', run: () => setChanging(true) },
                { label: 'Sign out', run: () => void signOut() },
              ]}
            />
          </div>
        )}
      </div>
      {changing && <ChangePassword onClose={() => setChanging(false)} />}
    </header>
  );
}

/** The note that shows when what's on screen came from the device rather than the server. */
export function SavedOnDevice({ at }: { at: number | null }) {
  if (!at) return null;
  return (
    <p className="offline-note" role="status">
      <Offline /> No connection: this is what the device saved {new Date(at).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}.
    </p>
  );
}
